import { useState } from 'react';
import { InputNumber, Switch } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { MAC_DINH_TAC_VU } from '../constants';
import { useLuuTacVu } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { docGioiHan, dongGoiCauHinh, giaTriBanDau, khungGioHopLe, kiemForm, THU_TU_KENH } from '../nghiep-vu/canh-bao';
import type { GiaTriForm } from '../nghiep-vu/canh-bao';
import type { NotifyAlarmTask, NotifyAlarmType, NotifyChannel, NotifyChannelType, PhamViCanhBao } from '../types';
import { OTruong } from './OTruong';

const GIAY_TOI_DA = 604_800;

/**
 * Hộp thêm hoặc sửa tác vụ cảnh báo (aaPanel: Add task). Thứ tự: loại → ô tham số dựng từ `params` của loại → tên →
 * cấu hình kênh gom theo loại kênh → "Cài đặt nâng cao" gập sẵn (giới hạn ngày, tổng, khoảng cách, khung giờ).
 */
export function HopTacVu({
  pv,
  loaiCanhBao,
  loaiKenh,
  kenh,
  dangCo,
  onClose,
  onMoCauHinhKenh,
}: {
  pv: PhamViCanhBao;
  loaiCanhBao: NotifyAlarmType[];
  loaiKenh: NotifyChannelType[];
  kenh: NotifyChannel[];
  dangCo: NotifyAlarmTask | null;
  onClose: () => void;
  onMoCauHinhKenh: () => void;
}) {
  const { t } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const luu = useLuuTacVu(pv);
  const [khoaLap] = useState(() => crypto.randomUUID());

  const [ma, setMa] = useState<string | undefined>(dangCo?.type_code);
  const loai = loaiCanhBao.find((l) => l.code === ma) ?? null;
  const [tieuDe, setTieuDe] = useState(dangCo?.title ?? '');
  const [daSuaTieuDe, setDaSuaTieuDe] = useState(Boolean(dangCo));
  const [thamSo, setThamSo] = useState<GiaTriForm>(() => (loai ? giaTriBanDau(loai.params, dangCo?.params) : {}));
  const [chon, setChon] = useState<string[]>(dangCo?.channel_ids ?? []);
  const [ngay, setNgay] = useState(String(dangCo ? (dangCo.daily_limit ?? '') : MAC_DINH_TAC_VU.daily_limit));
  const [tong, setTong] = useState(String(dangCo?.total_limit ?? ''));
  const [khoang, setKhoang] = useState<number | null>(dangCo?.min_interval_sec ?? MAC_DINH_TAC_VU.min_interval_sec);
  const [tu, setTu] = useState(dangCo?.send_from ?? MAC_DINH_TAC_VU.send_from);
  const [den, setDen] = useState(dangCo?.send_to ?? MAC_DINH_TAC_VU.send_to);
  const [bat, setBat] = useState(dangCo?.enabled ?? true);
  const [nangCao, setNangCao] = useState(false);
  const [daBam, setDaBam] = useState(false);

  const chonLoai = (code: string) => {
    const moi = loaiCanhBao.find((l) => l.code === code);
    setMa(code);
    setThamSo(moi ? giaTriBanDau(moi.params) : {});
    if (!daSuaTieuDe) setTieuDe(dich.tenLoai(code));
  };

  const loiThamSo = loai ? kiemForm(loai.params, thamSo) : {};
  const ngayDoc = docGioiHan(ngay);
  const tongDoc = docGioiHan(tong);
  const loi = {
    loai: !loai ? t('canhBao.loiChonLoai') : undefined,
    tieuDe: tieuDe.trim() === '' ? t('canhBao.loiTieuDe') : undefined,
    kenh: chon.length === 0 ? t('canhBao.loiChonKenh') : undefined,
    ngay: ngayDoc === undefined ? t('canhBao.loiGioiHan') : undefined,
    tong: tongDoc === undefined ? t('canhBao.loiGioiHan') : undefined,
    khoang: khoang == null || khoang < 0 || khoang > GIAY_TOI_DA ? t('canhBao.loiKhoangCach') : undefined,
    gio: !khungGioHopLe(tu, den) ? t('canhBao.loiKhungGio') : undefined,
  };
  const loiNangCao = Boolean(loi.ngay || loi.tong || loi.khoang || loi.gio);
  const hopLe = !Object.values(loi).some(Boolean) && Object.keys(loiThamSo).length === 0;

  const gui = () => {
    setDaBam(true);
    if (loiNangCao) setNangCao(true);
    if (!hopLe || !loai) return;
    const du = {
      type_code: loai.code,
      title: tieuDe.trim(),
      params: dongGoiCauHinh(loai.params, thamSo),
      channel_ids: chon,
      daily_limit: ngayDoc ?? null,
      total_limit: tongDoc ?? null,
      min_interval_sec: khoang ?? 0,
      send_from: tu,
      send_to: den,
      enabled: bat,
    };
    const lam = dangCo
      ? luu.mutateAsync({ tao: false, tacVu: dangCo, du })
      : luu.mutateAsync({ tao: true, du, khoaLap });
    lam.then(onClose).catch(() => undefined);
  };

  const loaiKenhSapXep = [...loaiKenh].sort((a, b) => THU_TU_KENH.indexOf(a.code) - THU_TU_KENH.indexOf(b.code));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(dangCo ? 'canhBao.hopSua' : 'canhBao.hopThem')}
      width={600}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={luu.isPending} onClick={gui}>
            {luu.isPending ? t('canhBao.dangLuu') : t(dangCo ? 'canhBao.nutLuuTacVu' : 'canhBao.nutThemTacVu')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <Field
          label={t('canhBao.truongLoai')}
          required
          hint={ma ? dich.moLoai(ma) : undefined}
          error={daBam ? loi.loai : undefined}
        >
          {(a11y) => (
            <Select<string>
              {...a11y}
              className="w-full"
              showSearch
              optionFilterProp="label"
              placeholder={t('canhBao.chonLoai')}
              value={ma}
              disabled={Boolean(dangCo)}
              onChange={chonLoai}
              options={loaiCanhBao.map((l) => ({
                value: l.code,
                label: dich.tenLoai(l.code),
              }))}
            />
          )}
        </Field>

        {loai?.params.map((p) => (
          <OTruong
            key={p.key}
            truong={p}
            nhan={dich.nhanThamSo(p.key)}
            giaTri={thamSo[p.key] ?? null}
            onChange={(v) => setThamSo((g) => ({ ...g, [p.key]: v }))}
            loi={daBam ? loiThamSo[p.key] : undefined}
            dichTuyChon={dich.nhanTuyChon}
          />
        ))}

        <Field
          label={t('canhBao.truongTieuDe')}
          required
          hint={t('canhBao.tieuDeGoiY')}
          error={daBam ? loi.tieuDe : undefined}
        >
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={150}
              value={tieuDe}
              onChange={(e) => {
                setTieuDe(e.target.value);
                setDaSuaTieuDe(true);
              }}
            />
          )}
        </Field>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-content-primary">
            {t('canhBao.truongKenh')} <span className="text-danger-600">*</span>
          </legend>
          <p className="text-xs text-content-muted">{t('canhBao.kenhGoiY')}</p>
          <div className="space-y-3 rounded-lg border border-default p-3">
            {loaiKenhSapXep.length === 0 && <p className="text-sm text-content-muted">{t('canhBao.chuaCoKenhNao')}</p>}
            {loaiKenhSapXep.map((lk) => {
              const ds = kenh.filter((k) => k.channel_type === lk.code);
              if (ds.length === 0) {
                return (
                  <div key={lk.code} className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
                    <span className="min-w-0">
                      {t('canhBao.chuaCauHinhLoai', {
                        kenh: dich.tenKenh(lk.code),
                      })}
                    </span>
                    <Button variant="link" size="sm" onClick={onMoCauHinhKenh}>
                      {t('canhBao.moCauHinhKenh')}
                    </Button>
                  </div>
                );
              }
              return (
                <div key={lk.code} className="space-y-1">
                  <p className="text-xs font-medium text-content-secondary">{dich.tenKenh(lk.code)}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {ds.map((k) => (
                      <Checkbox
                        key={k.id}
                        checked={chon.includes(k.id)}
                        onChange={(e) =>
                          setChon((c) => (e.target.checked ? [...c, k.id] : c.filter((x) => x !== k.id)))
                        }
                      >
                        <span className="text-sm">
                          {k.enabled ? k.name : t('canhBao.kenhDangTat', { ten: k.name })}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {daBam && loi.kenh && (
            <p role="alert" className="text-xs text-danger-600">
              {loi.kenh}
            </p>
          )}
        </fieldset>

        <div>
          <Button
            variant="ghost"
            size="sm"
            icon={nangCao ? <DownOutlined /> : <RightOutlined />}
            aria-expanded={nangCao}
            onClick={() => setNangCao((v) => !v)}
          >
            {t('canhBao.caiDatNangCao')}
          </Button>
          {nangCao && (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Field
                label={t('canhBao.toiDaNgay')}
                hint={t('canhBao.deTrongKhongGioiHan')}
                error={daBam ? loi.ngay : undefined}
              >
                {(a11y) => (
                  <Input {...a11y} inputMode="numeric" value={ngay} onChange={(e) => setNgay(e.target.value)} />
                )}
              </Field>
              <Field
                label={t('canhBao.toiDaTong')}
                hint={t('canhBao.deTrongKhongGioiHan')}
                error={daBam ? loi.tong : undefined}
              >
                {(a11y) => (
                  <Input {...a11y} inputMode="numeric" value={tong} onChange={(e) => setTong(e.target.value)} />
                )}
              </Field>
              <Field
                label={t('canhBao.khoangCach')}
                hint={t('canhBao.khoangCachGoiY')}
                error={daBam ? loi.khoang : undefined}
              >
                {(a11y) => (
                  <InputNumber
                    {...a11y}
                    className="w-full"
                    min={0}
                    max={GIAY_TOI_DA}
                    value={khoang}
                    onChange={(v) => setKhoang(v)}
                  />
                )}
              </Field>
              <div className="space-y-1.5 sm:col-span-2">
                <div className="flex flex-wrap items-end gap-2">
                  <Field label={t('canhBao.khungGioTu')} className="w-28">
                    {(a11y) => <Input {...a11y} value={tu} maxLength={5} onChange={(e) => setTu(e.target.value)} />}
                  </Field>
                  <Field label={t('canhBao.khungGioDen')} className="w-28">
                    {(a11y) => <Input {...a11y} value={den} maxLength={5} onChange={(e) => setDen(e.target.value)} />}
                  </Field>
                </div>
                {daBam && loi.gio ? (
                  <p role="alert" className="text-xs text-danger-600">
                    {loi.gio}
                  </p>
                ) : (
                  <p className="text-xs text-content-muted">{t('canhBao.khungGioGoiY')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <label className="flex items-center gap-3">
          <Switch checked={bat} onChange={setBat} aria-label={t('canhBao.batSauLuu')} />
          <span className="text-sm text-content-primary">{t('canhBao.batSauLuu')}</span>
        </label>

        {luu.error && <LoiTrongHop>{dichLoi(luu.error)}</LoiTrongHop>}
      </div>
    </Dialog>
  );
}
